import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../../components/ui';
import { errMsg, fmt } from '../../utils/format';
import { useFetch } from '../../hooks/useFetch';
import api from '../../utils/api';

export default function EditPurchaseOrderModal({ po, onClose, onSaved }) {
  const { data: vendors } = useFetch('/api/vendors');
  const { data: materials } = useFetch('/api/raw-materials');
  const { data: detail } = useFetch(`/api/purchase-orders/${po.id}`);

  const [vendorId, setVendorId] = useState(String(po.vendor_id || ''));
  const [notes, setNotes] = useState(po.notes || '');
  const [leadTimeDays, setLeadTimeDays] = useState(po.lead_time_days != null ? String(po.lead_time_days) : '');
  const [issuedDate, setIssuedDate] = useState(po.issued_date ? po.issued_date.slice(0,10) : '');
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (detail?.items?.length) {
      setItems(detail.items.map(i => ({
        materialId: String(i.material_id),
        quantity: String(i.quantity),
        unitPrice: String(i.unit_price),
        discountPct: String(i.discount_pct || 0),
        materialName: i.material_name,
      })));
    }
  }, [detail]);

  // Auto-apply vendor quantity discounts
  const [discountTiers, setDiscountTiers] = useState([]);
  useEffect(() => {
    if (!po.vendor_id) return;
    api.get(`/api/vendor-discounts?vendorId=${po.vendor_id}`)
      .then(r => setDiscountTiers(r.data || []))
      .catch(() => {});
  }, [po.vendor_id]);

  const getAutoDiscount = useCallback((qty) => {
    if (!discountTiers.length || !qty) return 0;
    const q = parseFloat(qty);
    const applicable = discountTiers
      .filter(t => q >= parseFloat(t.min_quantity))
      .sort((a,b) => b.min_quantity - a.min_quantity);
    return applicable.length ? parseFloat(applicable[0].discount_pct) : 0;
  }, [discountTiers]);

  const setItem = (idx,k,v) => setItems(i=>i.map((row,j) => {
    if (j !== idx) return row;
    const updated = {...row,[k]:v};
    if (k === 'quantity') {
      const auto = getAutoDiscount(v);
      updated.discountPct = String(auto);
    }
    return updated;
  }));
  const addItem = () => setItems(i=>[...i,{ materialId:'', quantity:'', unitPrice:'' }]);
  const removeItem = (idx) => setItems(i=>i.filter((_,j)=>j!==idx));

  const selectMaterial = (idx, materialId) => {
    const m = (materials||[]).find(m=>String(m.id)===materialId);
    setItems(i=>i.map((row,j)=>j===idx?{...row,materialId,unitPrice:m?String(m.unit_cost):row.unitPrice}:row));
  };

  const total = items.reduce((s,i)=>s+(parseFloat(i.quantity)||0)*(parseFloat(i.unitPrice)||0)*(1-(parseFloat(i.discountPct)||0)/100),0);

  const expectedArrival = (() => {
    if (!issuedDate || !leadTimeDays) return null;
    const d = new Date(issuedDate);
    d.setDate(d.getDate() + parseInt(leadTimeDays));
    return d.toISOString().slice(0,10);
  })();

  const save = async (e) => {
    e.preventDefault();
    const validItems = items.filter(i=>i.materialId && i.quantity && i.unitPrice);
    if (!validItems.length) { alert('Add at least one line item'); return; }
    setSaving(true);
    try {
      await api.patch(`/api/purchase-orders/${po.id}`, {
        vendorId: vendorId ? parseInt(vendorId) : null,
        notes,
        leadTimeDays: leadTimeDays ? parseInt(leadTimeDays) : null,
        issuedDate: issuedDate || null,
        items: validItems.map(i=>({ materialId:parseInt(i.materialId), quantity:parseFloat(i.quantity), unitPrice:parseFloat(i.unitPrice), discountPct:parseFloat(i.discountPct)||0 }))
      });
      onSaved('Purchase order updated successfully'); onClose();
    } catch(err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  const readOnly = ['received','cancelled'].includes(po.status);

  return (
    <Modal title={`Edit PO — ${po.po_number}`} onClose={onClose} wide
      footer={<><button className="btn" onClick={onClose}>Close</button>{!readOnly && <button className="btn btn-primary" form="edit-po-form" type="submit">{saving?'Saving…':'Save changes'}</button>}</>}>
      {readOnly && <div className="alert alert-warning" style={{ marginBottom:12 }}>This PO is {po.status} and cannot be edited.</div>}
      <form id="edit-po-form" onSubmit={save}>
        <div className="form-group">
          <label>Vendor</label>
          <select value={vendorId} onChange={e=>setVendorId(e.target.value)} disabled={readOnly}>
            <option value="">— Select vendor —</option>
            {(vendors||[]).map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginTop:8 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label>Issued date</label>
            <input type="date" value={issuedDate} onChange={e=>setIssuedDate(e.target.value)} disabled={readOnly} />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label>Lead time (days)</label>
            <input type="number" min="0" placeholder="e.g. 14" value={leadTimeDays} onChange={e=>setLeadTimeDays(e.target.value)} disabled={readOnly} />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label>Expected arrival</label>
            <input type="date" value={expectedArrival||''} readOnly style={{ background:'var(--bg-secondary)', color: expectedArrival ? 'var(--text-primary)' : 'var(--text-tertiary)' }} />
          </div>
        </div>

        <div style={{ fontWeight:500, fontSize:13, marginBottom:8, marginTop:16 }}>Line items</div>
        {!detail && <div style={{ color:'var(--text-tertiary)', fontSize:13 }}>Loading…</div>}
        {items.map((item,idx) => (
          <div key={idx} style={{ display:'grid', gridTemplateColumns:'3fr 1fr 1fr 0.7fr auto', gap:8, marginBottom:8, alignItems:'end' }}>
            <div className="form-group" style={{ margin:0 }}>
              {idx===0 && <label style={{ fontSize:11 }}>Raw material</label>}
              <select value={item.materialId} onChange={e=>selectMaterial(idx,e.target.value)} disabled={readOnly}>
                <option value="">— Select —</option>
                {(materials||[]).map(m=><option key={m.id} value={m.id}>{m.name} ({m.unit_of_measure})</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              {idx===0 && <label style={{ fontSize:11 }}>Quantity</label>}
              <input type="number" step="1" min="0" placeholder="Qty" value={item.quantity} onChange={e=>setItem(idx,'quantity',e.target.value)} disabled={readOnly} />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              {idx===0 && <label style={{ fontSize:11 }}>Unit price ($)</label>}
              <input type="number" step="0.0001" min="0" placeholder="Price" value={item.unitPrice} onChange={e=>setItem(idx,'unitPrice',e.target.value)} disabled={readOnly} />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              {idx===0 && <label style={{ fontSize:11 }}>Discount (%)</label>}
              <input type="number" step="0.01" min="0" max="100" placeholder="0" value={item.discountPct||'0'} onChange={e=>setItem(idx,'discountPct',e.target.value)} disabled={readOnly} />
            </div>
            {!readOnly && <button type="button" className="btn btn-sm btn-danger" style={{ marginTop:idx===0?18:0 }} onClick={()=>removeItem(idx)}>
              <i className="ti ti-x" />
            </button>}
          </div>
        ))}
        {!readOnly && <button type="button" className="btn btn-sm" onClick={addItem} style={{ marginTop:4 }}>
          <i className="ti ti-plus" /> Add line
        </button>}
        {total > 0 && <div style={{ textAlign:'right', marginTop:12, fontWeight:500 }}>Total: {fmt.currency(total)}</div>}
        <div className="form-group" style={{ marginTop:12 }}><label>Notes</label><textarea value={notes} onChange={e=>setNotes(e.target.value)} disabled={readOnly} /></div>
      </form>
    </Modal>
  );
}
